import { SkeletonPortofolio } from "@/components/SkeletonPortofolio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { web } from "@/config/System";
import useSessionStorage from "@/hooks/useSessionStorage";
import api from "@/lib/axios";
import { getDataStep } from "@/services/getData";
import { SysBerkasPortofolio } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  ArrowRight,
  CheckCircle2,
  Download,
  Dumbbell,
  ExternalLink,
  FileCheck,
  FileText,
  Info,
  Loader2,
  Music,
  UploadCloud,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";

// ... (API functions tetap sama) ...
const getListBerkasPortofolio = async (id_sys_periode: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/portofolio/index?id_sys_periode=${id_sys_periode}`,
  );
  return response.data?.data;
};
const uploadPortofolio = async (newData: FormData) => {
  const response = await api.post(
    "/siswa/penerimaan/smbjm/portofolio/upload",
    newData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};
const skipStepPortofolio = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/siswa/penerimaan/smbjm/portofolio/skip?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data;
};

export default function Portofolio({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const { data: dataStep } = useQuery({
    queryKey: ["dataStep", sessionPeriode?.id_sys_periode],
    queryFn: () => getDataStep(sessionPeriode?.id_sys_periode || ""),
  });

  const {
    data: listBerkasPortofolio,
    isPending: isPendingListBerkasPortofolio,
    isFetched: isFetchedLlistBerkasPortofolio,
  } = useQuery({
    queryKey: ["listBerkasPortofolio", sessionPeriode?.id_sys_periode],
    queryFn: () => getListBerkasPortofolio(sessionPeriode?.id_sys_periode),
  });

  const listBerkasPortofolioOlahraga =
    listBerkasPortofolio?.filter(
      (item: SysBerkasPortofolio) => item?.bidang === "olahraga",
    ) || [];
  const listBerkasPortofolioSeni =
    listBerkasPortofolio?.filter(
      (item: SysBerkasPortofolio) => item?.bidang === "seni",
    ) || [];

  const [selectedSysBerkasPortofolio, setSelectedSysBerkasPortofolio] =
    useState<SysBerkasPortofolio | null>(null);
  const [files, setFiles] = useState<{ [key: string]: File | null }>({});

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: uploadPortofolio,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listBerkasPortofolio"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      toast.success(data?.message || "Berhasil mengunggah portofolio.");
      // Reset file input state for this item after success
      if (selectedSysBerkasPortofolio) {
        setFiles((prev) => ({
          ...prev,
          [selectedSysBerkasPortofolio.slug_jenis_berkas]: null,
        }));
      }
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(
        error.response?.data?.message || "Gagal mengunggah portofolio.",
      );
    },
  });

  const { mutate: mutateSkipPortofolio, isPending: isPendingSkipPortofolio } =
    useMutation({
      mutationFn: skipStepPortofolio,
      onSuccess: (data) => {
        queryClient.invalidateQueries({ queryKey: ["dataStep"] });
        toast.success(data?.message || "Berhasil melewati langkah ini.");
      },
      onError: (error: AxiosError<{ message?: string }>) => {
        toast.error(
          error.response?.data?.message || "Gagal melewati langkah ini.",
        );
      },
    });

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    const file = e.target.files?.[0];
    if (file) setFiles((prev) => ({ ...prev, [key]: file }));
  };

  const handleUpload = (
    e: React.FormEvent,
    idSysBerkas: string,
    key: string,
  ) => {
    e.preventDefault();
    if (!files[key]) return; // Prevent upload if no file selected

    const formData = new FormData();
    formData.append("id_sys_berkas_portofolio", idSysBerkas);
    formData.append("id_data_pendaftaran", idDataPendaftaran);
    formData.append("file_portofolio", files[key]!); // Non-null assertion aman krn dicek diatas
    mutate(formData);
  };

  const isDonePortofolioOlahraga =
    listBerkasPortofolioOlahraga.length > 0 &&
    listBerkasPortofolioOlahraga.every(
      (b: any) => !!b.data_portofolio?.file_portofolio,
    );
  const isDonePortofolioSeni =
    listBerkasPortofolioSeni.length > 0 &&
    listBerkasPortofolioSeni.every(
      (b: any) => !!b.data_portofolio?.file_portofolio,
    );

  // --- Components ---

  const PortfolioSection = ({
    title,
    icon: Icon,
    listBerkas,
    isDone,
    guideLink,
    templateLink,
  }: any) => {
    if (!listBerkas || listBerkas.length === 0) return null;

    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm h-full flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-lg">
              <Icon className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              {title}
            </h2>
          </div>

          <div className="flex gap-2.5 mt-4">
            {/* Panduan Button - Soft Blue to indicate Information/Help */}
            <Button
              variant="secondary" // Use secondary or ghost as base if you want to override colors
              size="sm"
              className="h-8 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:text-blue-800 hover:border-blue-300 transition-colors shadow-sm dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/50 dark:hover:bg-blue-900/40"
              onClick={() => window.open(guideLink, "_blank")}
            >
              <Info className="w-3.5 h-3.5 mr-1.5" />
              Lihat Panduan
            </Button>

            {/* Template Button - Soft Green/Emerald to indicate Action/Download */}
            <Button
              variant="secondary"
              size="sm"
              className="h-8 text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:text-emerald-800 hover:border-emerald-300 transition-colors shadow-sm dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50 dark:hover:bg-emerald-900/40"
              onClick={() => window.open(templateLink, "_blank")}
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Unduh Template
            </Button>
          </div>
        </div>

        {/* Content List */}
        <div className="p-6 space-y-6 flex-1">
          {listBerkas.map((berkas: SysBerkasPortofolio) => {
            const fileUrl = berkas.data_portofolio?.file_portofolio;
            const isUploaded = !!fileUrl;
            const selectedFile = files[berkas.slug_jenis_berkas];
            const isUploading =
              isPending &&
              selectedSysBerkasPortofolio?.id_sys_berkas_portofolio ===
                berkas.id_sys_berkas_portofolio;

            return (
              <div
                key={berkas.id_sys_berkas_portofolio}
                className="relative pl-6 border-l-2 border-slate-200 dark:border-slate-800 last:pb-0 pb-6"
              >
                {/* Step Indicator */}
                <div
                  className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 ${
                    isUploaded
                      ? "bg-emerald-500 border-emerald-500"
                      : "bg-white border-slate-300 dark:border-slate-600 dark:bg-slate-900"
                  }`}
                >
                  {isUploaded && (
                    <CheckCircle2 className="w-full h-full text-white p-0.5" />
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="font-semibold text-base">
                      {berkas.nama_jenis_berkas}
                    </Label>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {berkas.keterangan_berkas}
                    </p>
                    <div className="flex gap-2 mt-2">
                      {berkas.extension_allowed?.map((ext: string) => (
                        <Badge
                          key={ext}
                          variant="secondary"
                          className="text-[10px] uppercase px-1.5 h-5"
                        >
                          {ext}
                        </Badge>
                      ))}
                      <Badge variant="outline" className="text-[10px] h-5">
                        Max {Number(berkas.max_upload / 1024)} MB
                      </Badge>
                    </div>
                  </div>

                  <form
                    onSubmit={(e) => {
                      setSelectedSysBerkasPortofolio(berkas);
                      handleUpload(
                        e,
                        berkas.id_sys_berkas_portofolio,
                        berkas.slug_jenis_berkas,
                      );
                    }}
                    className={`p-4 rounded-lg border border-dashed transition-all ${
                      selectedFile
                        ? "border-blue-400 bg-blue-50/50 dark:bg-blue-900/10"
                        : "border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950"
                    }`}
                  >
                    {/* Input File Hidden, Trigger via Label */}
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {selectedFile ? (
                            <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                              <FileText className="w-4 h-4 shrink-0" />
                              <span className="truncate font-medium">
                                {selectedFile.name}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-slate-500 italic">
                              {isUploaded ? "" : "Belum ada file dipilih..."}
                            </span>
                          )}
                        </div>

                        <Label
                          htmlFor={`file-${berkas.id_sys_berkas_portofolio}`}
                          className="cursor-pointer text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors whitespace-nowrap"
                        >
                          Pilih {isUploaded ? "Ulang" : ""} File
                        </Label>
                        <Input
                          id={`file-${berkas.id_sys_berkas_portofolio}`}
                          type="file"
                          className="hidden"
                          onChange={(e) =>
                            handleFileChange(e, berkas.slug_jenis_berkas)
                          }
                          accept={berkas.extension_allowed
                            ?.map((e) => `.${e}`)
                            .join(",")}
                        />
                      </div>

                      {/* Action Buttons Area */}
                      <div className="flex flex-wrap items-center justify-between border-t border-slate-200/50 dark:border-slate-700/50 pt-3 mt-1">
                        <div>
                          {isUploaded && (
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-xs text-blue-600"
                              onClick={() => window.open(fileUrl, "_blank")}
                            >
                              <ExternalLink className="w-3 h-3 mr-1" /> Lihat
                              File Lama
                            </Button>
                          )}
                        </div>

                        <Button
                          type="submit"
                          size="sm"
                          disabled={!selectedFile || isUploading} // Disable if no file selected
                          className={`text-xs h-8 ${
                            selectedFile
                              ? "bg-blue-600 hover:bg-blue-700 text-white"
                              : "bg-slate-200 text-slate-400 cursor-not-allowed dark:bg-slate-800 dark:text-slate-600"
                          }`}
                        >
                          {isUploading ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <UploadCloud className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          {isUploaded ? "Upload Ulang" : "Upload"}
                        </Button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Status */}
        {isDone && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 border-t border-emerald-100 dark:border-emerald-800 flex items-center justify-center gap-2 text-emerald-700 dark:text-emerald-400 font-medium text-sm">
            <CheckCircle2 className="w-5 h-5" /> Semua berkas lengkap!
          </div>
        )}
      </div>
    );
  };

  if (isPendingListBerkasPortofolio) return <SkeletonPortofolio />;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* HEADER */}
      <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 pb-4">
        <FileCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            Portofolio
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Unggah bukti bakat dan keterampilan sesuai program studi.
          </p>
        </div>
      </div>

      {/* EMPTY STATE */}
      {isFetchedLlistBerkasPortofolio && listBerkasPortofolio?.length === 0 && (
        <div className="flex flex-col items-center justify-center p-10 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50/50 dark:bg-slate-900/20">
          <div className="p-4 bg-white dark:bg-slate-900 rounded-full shadow-sm mb-4">
            <Info className="w-10 h-10 text-slate-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">
            Tidak Ada Portofolio Diperlukan
          </h3>
          <p className="text-sm text-slate-500 max-w-md mb-6">
            Program studi pilihan Anda tidak mewajibkan portofolio. Silakan
            lanjutkan.
          </p>

          {dataStep?.data_portofolio !== 1 && (
            <Button
              size="lg"
              onClick={() => mutateSkipPortofolio(idDataPendaftaran)}
              disabled={isPendingSkipPortofolio}
            >
              {isPendingSkipPortofolio ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                "Lanjut"
              )}{" "}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      )}

      {/* MAIN CONTENT */}
      {(listBerkasPortofolioOlahraga.length > 0 ||
        listBerkasPortofolioSeni.length > 0) && (
        <div
          className={`grid gap-8 items-start ${
            listBerkasPortofolioOlahraga.length > 0 &&
            listBerkasPortofolioSeni.length > 0
              ? "lg:grid-cols-2"
              : "grid-cols-1"
          }`}
        >
          <PortfolioSection
            title="Portofolio Olahraga"
            icon={Dumbbell}
            listBerkas={listBerkasPortofolioOlahraga}
            isDone={isDonePortofolioOlahraga}
            guideLink={web.linkInstruksiPortofolioOlahraga}
            templateLink={web.linkTemplatePortofolioOlahraga}
          />

          <PortfolioSection
            title="Portofolio Seni"
            icon={Music}
            listBerkas={listBerkasPortofolioSeni}
            isDone={isDonePortofolioSeni}
            guideLink={web.linkInstruksiPortofolioSeni}
            templateLink={web.linkTemplatePortofolioSeni}
          />
        </div>
      )}
    </div>
  );
}
